import React from 'react';
import { RouterProvider } from 'react-router';

import { ensureCsrfToken } from 'api/csrf';
import { removeLegacyAuthToken } from 'api/tokenStorage';
import { browserRouter } from 'router';
import { isAuthSessionActive, useAuthSession } from 'hooks/useAuthSession';

import { AuthContext, type AuthContextValue } from './useAuth';

const BOOT_DEADLINE_MS = 12_000;

export const AppInternal = ({ onReady }: { onReady?: () => void }) => {
  const [csrfReady, setCsrfReady] = React.useState(false);
  const [bootGaveUp, setBootGaveUp] = React.useState(false);
  // Сессия не ждет CSRF: иначе зависший /auth/csrf/ держит спиннер /login и /registration.
  const { data: session, isLoading, isFetching, isError } = useAuthSession(true);
  const sessionSettled = !isLoading && !(isFetching && !session);
  const shellReady = csrfReady || bootGaveUp || sessionSettled || isError;

  React.useEffect(() => {
    if ((shellReady && (sessionSettled || isError)) || bootGaveUp) onReady?.();
  }, [shellReady, sessionSettled, isError, bootGaveUp, onReady]);

  React.useEffect(() => {
    removeLegacyAuthToken();
    const timer = window.setTimeout(() => setBootGaveUp(true), BOOT_DEADLINE_MS);
    ensureCsrfToken(true)
      .catch(() => undefined)
      .finally(() => setCsrfReady(true));
    return () => window.clearTimeout(timer);
  }, []);

  const signed = isAuthSessionActive(session);

  const authContextValue = React.useMemo<AuthContextValue>(
    () => ({
      signed,
      setSigned: () => {},
    }),
    [signed],
  );

  if (!shellReady) {
    return null;
  }

  return (
    <AuthContext value={authContextValue}>
      <RouterProvider router={browserRouter} />
    </AuthContext>
  );
};
